import React, { useContext, useEffect, useState } from "react";
import {
  Modal,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  Platform,
} from "react-native";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Button } from "react-native-paper";
import { Picker } from "react-native";
import { useToast } from "react-native-toast-notifications";
import { orderUsersModal } from "../../../models/orderUsersModal";
import {
  getAllOperations,
  updatedirectOperationOrderStatus,
  updateProcessingPerson,
} from "../../../services/operationService";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { headers, mdHeaders, smHeaders } from "../utils/operationHeaders";
import Spinner from "../../../components/shared/Spinner";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: () => void;
  order: any;
}

interface FormData {
  operationId: string;
  orderStatus: string;
}

const OperationStatusUpdateModal: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}) => {
  const orderStatusList = [
    {
      key: "Draft",
      value: "Draft",
    },
    {
      key: "Pending",
      value: "Pending",
    },
    {
      key: "Dispatched",
      value: "Dispatched",
    },
    {
      key: "Cancelled",
      value: "Cancelled",
    },
    {
      key: "Out Of Stock",
      value: "Out Of Stock",
    },
    {
      key: "Pending for Payment",
      value: "Pending for Payment",
    },
  ];
  const [loading, setLoading] = useState<boolean>(false);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers
    );
  const toast = useToast();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      operationId: order._id,
      orderStatus: order?.orderStatus || "",
    },
  });

  const { isLargeDevice, isMediumDevice } = useContext(
    DeviceOrientationContext
  );

  useEffect(() => {
    setTableHeaders(
      isLargeDevice ? headers : isMediumDevice ? mdHeaders : smHeaders
    );
  }, []);

  const onSubmit = async (data: FormData) => {
    const payload: FormData = {
      operationId: data.operationId,
      orderStatus: data.orderStatus,
    };

    try {
      setLoading(true);

      await updatedirectOperationOrderStatus(payload)
        .then(() => {
          toast.show("Order Status Updated successfully", {
            type: "success",
          });
          onClose(); // Close modal after success
          getAllOperations(tableHeaders);
        })
        .catch(() => {
          toast.show("Error updating order status", {
            type: "danger",
          });
        });
    } catch (err) {
      console.error(" Order Status update failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Text style={styles.title}>
              Update Order Status: {order?.documentNumber}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          {loading ? (
            <Spinner
              size="small"
              color="#74BBFF"
              style={{ flexDirection: "row" }}
            />
          ) : (
            <>
              {orderStatusList?.some(
                (status: any) => status.value === order?.orderStatus
              ) ? (
                <>
                  <Text style={styles.inputLabel}>Select Order Status: </Text>

                  <Controller
                    control={control}
                    name="orderStatus"
                    rules={{ required: "Order Status is required" }}
                    render={({ field: { onChange, value } }) => (
                      <View
                        style={[
                          styles.pickerWrapper,
                          Platform.OS === "android" && {
                            borderWidth: 1.5,
                            borderColor: "#a6a6a6",
                            borderRadius: 10,
                          },
                        ]}
                      >
                        <Picker
                          selectedValue={value}
                          onValueChange={onChange}
                          style={styles.picker}
                        >
                          <Picker.Item
                            label="-- Select Order Status --"
                            value=""
                          />
                          {orderStatusList?.map(
                            (status: any, index: number) => (
                              <Picker.Item
                                key={index}
                                label={status.key}
                                value={status.value}
                              />
                            )
                          )}
                        </Picker>
                      </View>
                    )}
                  />
                  {errors.orderStatus && (
                    <Text style={styles.errorStyle}>
                      {errors.orderStatus.message}
                    </Text>
                  )}

                  <Button
                    mode="contained"
                    onPress={handleSubmit(onSubmit)}
                    style={styles.submitButton}
                  >
                    Submit
                  </Button>
                </>
              ) : (
                <Text style={{ color: "red" }}>
                  Cant' update the order status :{order.orderStatus}
                </Text>
              )}
            </>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 25,
    width: 500,
    borderRadius: 10,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    flexShrink: 1,
  },
  closeButton: {
    backgroundColor: "#1E88E5",
    borderRadius: 16,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  closeButtonText: {
    color: "#fff",
    fontSize: 16,
  },
  inputLabel: {
    fontWeight: "bold",
    marginBottom: 5,
    marginTop: 20,
    fontSize: 14,
  },
  pickerWrapper: {
    marginBottom: 10,
  },
  picker: {
    height: 50,
    width: "100%",
  },
  errorStyle: {
    color: "red",
    fontSize: 13,
    marginBottom: 10,
  },
  submitButton: {
    marginTop: 15,
    backgroundColor: "#000",
  },
});

export default OperationStatusUpdateModal;
